import { Listing, ListingBid } from "@/types";
import currencyFormat from "@/util/currencyFormat";

export interface BidStatusBadge {
  label: string;
  /** Tailwind background class for the status dot. */
  dot: string;
  /** Tailwind text colour class for the label. */
  text: string;
  /** Secondary line shown after the label, or null. */
  detail: string | null;
  /** Text joining label and detail when read as one phrase; defaults to " · ". */
  separator?: string;
}

// One colour per outcome so a column of statuses scans at a glance:
// green = won, sky = waiting, amber = missed, grey = withdrawn, red = problem.
const WON = { dot: "bg-boba-green-dark", text: "text-boba-brown" };
const PENDING = { dot: "bg-sky-500", text: "text-sky-800" };
const LOST = { dot: "bg-amber-500", text: "text-amber-800" };
const NEUTRAL = { dot: "bg-boba-grey", text: "text-boba-grey-dark" };
const FAILED = { dot: "bg-red-600", text: "text-red-800" };

/** How to present a bid's outcome to its owner. */
export function describeBidStatus(listing: Listing, bid: ListingBid): BidStatusBadge {
  if (!listing.isLowBid() || !bid.bid_status) {
    return { label: "Purchased", ...WON, detail: null };
  }

  switch (bid.bid_status) {
    case "active":
      if (listing.hasEnded()) {
        return { label: "Pending", ...PENDING, detail: null };
      }
      return { label: "Active", ...WON, detail: null };
    case "cancelled":
      return { label: "Cancelled", ...NEUTRAL, detail: null };
    case "lost":
      return { label: "Did not clear", ...LOST, detail: null };
    case "unfunded":
      return { label: "Won · payment failed", ...FAILED, detail: "(insufficient miles)", separator: " " };
    case "failed":
      return { label: "Won · payment failed", ...FAILED, detail: null };
    case "won": {
      if (bid.filled_quantity == null) {
        return { label: "Won", ...WON, detail: null };
      }
      const price = currencyFormat((listing.clearing_price ?? 0) / 1e9);
      return {
        label: "Won",
        ...WON,
        detail: `${currencyFormat(bid.filled_quantity)} of ${currencyFormat(bid.bid_quantity)} filled at ${price} miles/ea`,
      };
    }
    default:
      return { label: String(bid.bid_status), ...NEUTRAL, detail: null };
  }
}
